import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { desvincularPushAlSalir } from '../utils/push';

// Types
export interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  phone?: string;
  isActive: boolean;
  lastLogin?: string;
  avatar?: string;
  areaId?: string | null;
  areaName?: string | null;
  permissions: string[] | Record<string, any>;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  hasPermission: (permission: string) => boolean;
  hasRole: (roles: string | string[]) => boolean;
}

const normalizePermissions = (value: User['permissions'] | undefined): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return Array.from(new Set(value.map((v) => String(v).trim()).filter(Boolean)));
  }
  if (typeof value === 'object') {
    return Object.entries(value)
      .filter(([, enabled]) => Boolean(enabled))
      .map(([perm]) => String(perm).trim())
      .filter(Boolean);
  }
  return [];
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Axios configuration
const defaultApiBaseUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000';
axios.defaults.baseURL = (process.env.REACT_APP_API_URL || defaultApiBaseUrl).replace(/\/$/, '');

// Add token to requests
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiration
// No redirigir a /login si estamos en el monitor del taller (tiene auth propia con PIN)
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const isMonitorPage = window.location.pathname.startsWith('/taller/monitor');
      const isMonitorApi = error.config?.url?.includes('/workshop/monitor/');
      const isUploadPage = window.location.pathname.startsWith('/upload-photos');
      if (!isMonitorPage && !isMonitorApi && !isUploadPage) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Sesión vencida: se va a iniciar sesión recordando a dónde iba (por ejemplo, al
        // tocar una notificación), para volver ahí después de entrar. Si ya está en la
        // pantalla de inicio de sesión no se recarga: se perdería ese destino.
        if (window.location.pathname !== '/login') {
          const destino = `${window.location.pathname}${window.location.search}${window.location.hash}`;
          const volver = destino !== '/' && destino !== '/dashboard' ? `?returnTo=${encodeURIComponent(destino)}` : '';
          window.location.href = `/login${volver}`;
        }
      }
    }
    return Promise.reject(error);
  }
);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Check if user is logged in on app start
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');

      if (token && savedUser) {
        try {
          // Verify token with backend
          const response = await axios.get('/api/auth/me');
          const me = response.data.user;
          const storedAvatar = localStorage.getItem(`userAvatar-${me.id}`);
          const resolvedAvatar = me.avatar || storedAvatar;
          setUser(resolvedAvatar ? { ...me, avatar: resolvedAvatar } : me);
        } catch (error) {
          console.error('Token verification failed:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      setLoading(true);
      console.log('🔐 Intentando login para:', username);
      console.log('🔗 API URL:', axios.defaults.baseURL);
      
      const response = await axios.post('/api/auth/login', { username, password });
      
      console.log('✅ Respuesta del servidor:', response.data);
      
      // Verificar si requiere 2FA
      if (response.data.requires2FA) {
        console.log('🔒 Se requiere 2FA');
        // Lanzar un error especial para que el componente Login lo maneje
        const error: any = new Error('2FA required');
        error.response = { data: { requires2FA: true } };
        throw error;
      }
      
      const { token, user: userData } = response.data;
      
      if (!token || !userData) {
        console.error('❌ Respuesta inválida: falta token o user');
        throw new Error('Respuesta inválida del servidor');
      }
      
      console.log('✅ Token recibido, guardando en localStorage');
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      const storedAvatar = localStorage.getItem(`userAvatar-${userData.id}`);
      const resolvedAvatar = userData.avatar || storedAvatar;
      setUser(resolvedAvatar ? { ...userData, avatar: resolvedAvatar } : userData);
      toast.success('¡Bienvenido!');
      
      return true;
    } catch (error: any) {
      console.error('❌ Login error:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error message:', error.message);
      
      // Si es un error de 2FA, relanzarlo para que el componente lo maneje
      if (error.response?.data?.requires2FA) {
        console.log('🔒 Relanzando error de 2FA');
        throw error;
      }
      
      const errorMessage = error.response?.data?.error || error.message || 'Error al iniciar sesión';
      toast.error(errorMessage);
      
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    // Este dispositivo deja de recibir los avisos push de quien cierra sesión
    desvincularPushAlSalir(localStorage.getItem('token'));
    // El próximo que entre empieza por el inicio, no por la página de quien salió (ver ProtectedRoute)
    try { sessionStorage.setItem('rocam-cerro-sesion', String(Date.now())); } catch { /* sin storage */ }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    toast.success('Sesión cerrada');
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      if (updatedUser.avatar) {
        localStorage.setItem(`userAvatar-${updatedUser.id}`, updatedUser.avatar);
      }
    }
  };

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    const permissions = normalizePermissions(user.permissions);
    return permissions.includes('*') || permissions.includes(permission);
  };

  const hasRole = (roles: string | string[]): boolean => {
    if (!user) return false;
    
    const roleArray = Array.isArray(roles) ? roles : [roles];
    return roleArray.includes(user.role);
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    updateUser,
    hasPermission,
    hasRole
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 
